<template>
  <div ref="renderTarget" class="mmm-header-nav-teaser" v-if="active" aria-hidden="true">
    <div class="mmm-header-nav-teaser__wrapper">
      <div class="mmm-header-nav-teaser__row">
        <img
          :src="image"
          v-if="image"
          :alt="alt"
          @error="active = false"
          class="mmm-header-nav-teaser__image"
        />
      </div>
      <div class="mmm-header-nav-teaser__row" v-if="title && title.length > 0">
        <span class="mmm-header-nav-teaser__title">{{ sanitize(title) }}</span>
      </div>
      <div class="mmm-header-nav-teaser__row" v-if="text && text.length > 0">
        <span class="mmm-header-nav-teaser__text">{{ sanitize(text) }}</span>
      </div>
      <div class="mmm-header-nav-teaser__row">
        <a
          :href="target"
          class="mmm-header-nav-teaser__link"
          :aria-label="ariaLabel"
          ref="button"
          tabindex="-1"
          >{{ sanitize(label) }}</a
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue';

import { NavTeaserService, sanitize } from '../helper';

const props = defineProps<{
  navTeaserSelector: string;
}>();

const renderTarget = ref<HTMLElement | null>(null);
const image = ref<string>('');
const title = ref<string>('');
const text = ref<string>('');
const target = ref<string>('');
const alt = ref<string>('');
const active = ref<boolean>(true);
const label = ref<string>('Mehr erfahren');
const ariaLabel = ref<string>('Mehr erfahren');

const button = ref<HTMLElement>();

async function setValues() {
  active.value = true;
  const documentFragments = await window.publicApi.getPageConfiguration();
  if (!renderTarget.value) return;
  const teaserElement = documentFragments.querySelector(`[data-tag='${props.navTeaserSelector}']`);
  if (teaserElement) {
    image.value = teaserElement.querySelector('img')?.src || '';
    title.value = teaserElement.querySelector('h2')?.textContent || '';
    target.value = teaserElement.querySelector('a')?.href || '';
    label.value = teaserElement.querySelector('a')?.textContent || 'Mehr erfahren';
    ariaLabel.value = teaserElement.querySelector('a')?.textContent || 'Mehr erfahren';
    text.value = teaserElement.querySelector('p')?.textContent || '';
    alt.value = teaserElement.querySelector('a')?.getAttribute('alt') || title.value;
  } else {
    const currentTeaser = NavTeaserService.teaserConfig[props.navTeaserSelector];
    if (!currentTeaser) {
      active.value = false;
      return;
    }
    const imageKey = window.location.href.includes('preview-homepage-z') ? 'preview' : 'live';
    image.value = currentTeaser.image[imageKey];
    title.value = currentTeaser.title;
    text.value = currentTeaser.text;
    target.value = currentTeaser.link;
    alt.value = currentTeaser.alt || title.value;
    label.value = currentTeaser.label;
    ariaLabel.value = currentTeaser.ariaLabel;
  }
}

function focusButton() {
  button.value?.focus();
}

onMounted(async () => {
  await setValues();
});

watch(
  () => props.navTeaserSelector,
  () => {
    setValues();
  }
);

defineExpose({ focusButton });

export interface INavTeaserComponent {
  focusButton(): void;
}
</script>

<style lang="scss" scoped>
.mmm-header-nav-teaser {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 450px;

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 360px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    flex: 0 0 360px;
  }
  &__text {
    flex: 0 0 360px;
  }

  &__image {
    height: auto;
    max-height: 202px;
    max-width: 360px;
    width: auto;
    border-radius: 5px;
  }

  &__link {
    display: inline-block;
    padding: 12px 36px;
    background-color: var(--current-accent);
    color: white;
    font-weight: 700;
    border-radius: 5px;
    font-size: 16px;
    cursor: pointer;
    text-decoration: none;
    outline-offset: 2px;
  }
}
</style>
